/**
 * ERPNext Address Management API
 * User profile address CRUD operations
 */

const ERPNEXT_URL = import.meta.env.VITE_ERPNEXT_URL || '';

export interface Address {
  name: string;
  address_title: string;
  address_type: 'Billing' | 'Shipping' | 'Office' | 'Personal' | 'Other';
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  country: string;
  pincode: string;
  phone?: string;
  email_id?: string;
  is_primary_address?: boolean;
  is_shipping_address?: boolean;
  display?: string; // HTML formatted address from ERPNext (optional, we build our own)
}

export interface AddressInput {
  address_title: string;
  address_type: 'Billing' | 'Shipping';
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  country: string;
  pincode: string;
  phone?: string;
}

/**
 * Get all addresses for current user
 */
export async function getAddresses(): Promise<Address[]> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.get_shipping_addresses`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch addresses');
    }

    const data = await response.json();
    console.log('Raw addresses API response:', data);
    console.log('Addresses message:', data.message);

    // ERPNext returns {name, title, display} - map to our interface
    const addresses = data.message || [];
    const mappedAddresses = addresses.map((addr: any) => ({
      ...addr,
      address_title: addr.title || addr.address_title,
      address_type: addr.address_type || 'Shipping',
    }));

    console.log('Mapped addresses:', mappedAddresses);
    return mappedAddresses;
  } catch (error) {
    console.error('Get addresses error:', error);
    throw error;
  }
}

/**
 * Create a new address
 */
export async function createAddress(address: AddressInput): Promise<Address> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/method/webshop.webshop.shopping_cart.cart.add_new_address`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          doc: {
            doctype: 'Address',
            ...address,
          },
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create address');
    }

    const data = await response.json();
    return data.message;
  } catch (error) {
    console.error('Create address error:', error);
    throw error;
  }
}

/**
 * Update an existing address
 */
export async function updateAddress(
  addressName: string,
  updates: Partial<AddressInput>
): Promise<Address> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/resource/Address/${addressName}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(updates),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update address');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Update address error:', error);
    throw error;
  }
}

/**
 * Delete an address
 */
export async function deleteAddress(addressName: string): Promise<void> {
  try {
    const response = await fetch(
      `${ERPNEXT_URL}/api/resource/Address/${addressName}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete address');
    }
  } catch (error) {
    console.error('Delete address error:', error);
    throw error;
  }
}

/**
 * Set address as primary
 */
export async function setPrimaryAddress(
  addressName: string,
  isPrimary: boolean
): Promise<void> {
  try {
    await updateAddress(addressName, {
      // @ts-ignore
      is_primary_address: isPrimary ? 1 : 0,
    });
  } catch (error) {
    console.error('Set primary address error:', error);
    throw error;
  }
}
